import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

import usePageTitle from '../../hooks/UsePageTitle';
const ForensicAccounting = () => {
  usePageTitle('Forensic Accounting & Fraud Detection');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Forensic Accounting & Fraud Detection</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/financing1.png' 
            alt="Forensic Accounting Services" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              In today's fast-moving business landscape, financial frauds have become more sophisticated, 
              often concealed behind complex transactions, manipulated books, and collusive behavior.
            </p>
            <p className="text-lg line-height-3 mt-3">
              Whether you're an investor, board member, or management team, early detection and investigation 
              of anomalies can protect your organization from severe financial, legal, and reputational fallout.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              At SnP Finvisors, we specialize in Forensic Accounting & Fraud Detection services that go beyond 
              traditional audits. We use investigative accounting techniques, forensic tools, and domain expertise 
              to trace fraud, quantify impact, and support litigation or recovery actions.
            </p>
          </Card>
        </div>

        {/* Why Forensic Accounting Matters Section */}
        <div className="col-12 mt-5">
          <Card>
            <h2>Why Forensic Accounting Matters</h2>
            <Divider />
            <p className="text-lg line-height-3">
              Traditional audits are not designed to detect fraud. Fraud is often:
            </p>
            <ul className="list-none p-0 m-0 mt-3">
              <li className="mb-2 flex align-items-start">
                <i className="pi pi-circle-fill text-primary mr-2 mt-1" style={{ fontSize: '0.5rem' }}></i>
                <span>Concealed deliberately through override of controls or collusion</span>
              </li>
              <li className="mb-2 flex align-items-start">
                <i className="pi pi-circle-fill text-primary mr-2 mt-1" style={{ fontSize: '0.5rem' }}></i>
                <span>Identified only post-facto during investor exits or regulatory scrutiny</span>
              </li>
              <li className="flex align-items-start">
                <i className="pi pi-circle-fill text-primary mr-2 mt-1" style={{ fontSize: '0.5rem' }}></i>
                <span>Financially and reputationally damaging, especially for growing businesses</span>
              </li>
            </ul>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              Our forensic approach provides fact-based clarity in times of suspicion, dispute, or legal proceedings.
            </p>
          </Card>
        </div>

        {/* Services Section */}
        <div className="col-12 mt-5">
          <h2>Scope of Our Forensic Accounting Services</h2>
          <Divider />
          <p className="text-lg mb-5">
            We offer end-to-end fraud investigation support, from detection to documentation and resolution.
          </p>
          
          <div className="grid">
            {/* Service 1 */}
            <div className="col-12 md:col-6">
              <Card title="1. Fraud Investigation & Forensic Audit" className="mb-4 h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>In-depth forensic examination of financial statements and ERP logs</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Tracing of suspicious transactions, dummy vendors, or shell entities</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Detection of round-tripping, fund diversion, revenue suppression</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Examination of books, emails, metadata, and audit trails</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 2 */}
            <div className="col-12 md:col-6">
              <Card title="2. Digital Forensics & Data Analytics" className="mb-4 h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Use of forensic tools to extract, analyze, and visualize large volumes of data</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Pattern analysis, keyword triggers, and exception testing</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Recovery of deleted records, emails, and digital evidence</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 3 */}
            <div className="col-12 md:col-6">
              <Card title="3. Employee & Vendor Due Diligence" className="mb-4 h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Background verification and red-flag checks</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Related party mapping and undisclosed connections</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Analysis of kickback schemes, conflict of interest, and lifestyle mismatches</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 4 */}
            <div className="col-12 md:col-6">
              <Card title="4. Asset Tracing & Recovery Support" className="mb-4 h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of siphoned assets or diverted funds</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Tracing of beneficiary accounts and real estate holdings</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Support in legal recovery and coordination with enforcement agencies</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 5 */}
            <div className="col-12">
              <Card title="5. Litigation Support & Expert Reports" className="mb-4">
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <ul className="list-none p-0 m-0">
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Preparation of forensic reports admissible in court/arbitration</span>
                      </li>
                      <li className="mb-2 flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Support to legal counsel with data, documents, and deposition</span>
                      </li>
                      <li className="flex align-items-start">
                        <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                        <span>Representation assistance in regulatory investigations</span>
                      </li>
                    </ul>
                  </div>
                  <div className="col-12 md:col-6">
                    <img 
                      src='/images/financing2.png'
                      alt="Litigation Support" 
                      className="w-full border-round" 
                      style={{ maxHeight: '200px', objectFit: 'cover' }}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <Card className="surface-100">
            <h2 className="mt-0">Uncover the Truth with Forensic Precision</h2>
            <Divider />
            <div className="grid">
              <div className="col-12 md:col-8">
                <p className="text-xl line-height-3">
                  At SnP Finvisors, we combine financial expertise with investigative rigor to help you detect, 
                  document, and resolve financial frauds. Our forensic accounting services provide the clarity 
                  and evidence needed to protect your organization's financial integrity.
                </p>
              </div>
              <div className="col-12 md:col-4">
                <img 
                  src='/images/financing3.png' 
                  alt="Forensic Accounting" 
                  className="w-full border-round" 
                  style={{ maxHeight: '200px', objectFit: 'cover' }}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ForensicAccounting;